
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import {
  GoogleGenAI,
  Video,
  VideoGenerationReferenceImage,
  VideoGenerationReferenceType,
} from '@google/genai';
import {GenerateVideoParams, GenerationMode, VeoModel, Resolution, AspectRatio} from '../types';

export const generateVideo = async (
  params: GenerateVideoParams,
): Promise<{objectUrl: string; blob: Blob; uri: string; video: Video}> => {
  console.log('Starting video generation with params:', params);

  // CRITICAL: Create a new instance right before making an API call to ensure 
  // it always uses the most up-to-date API key from the dialog.
  const ai = new GoogleGenAI({apiKey: process.env.API_KEY});

  // API CONSTRAINT: If using reference images, model must be VEO (not VEO_FAST), 
  // resolution must be 720p, and aspect ratio must be 16:9.
  const hasUserReferences = params.referenceImages && params.referenceImages.length > 0;
  
  const effectiveModel = hasUserReferences ? VeoModel.VEO : params.model;
  const effectiveResolution = hasUserReferences ? Resolution.P720 : params.resolution;
  const effectiveAspectRatio = hasUserReferences ? AspectRatio.LANDSCAPE : params.aspectRatio;

  const config: any = {
    numberOfVideos: 1,
    resolution: effectiveResolution,
  };

  // Only set aspectRatio if not extending (extensions inherit from previous video)
  if (params.mode !== GenerationMode.EXTEND_VIDEO) {
    config.aspectRatio = effectiveAspectRatio;
  }

  const generateVideoPayload: any = {
    model: effectiveModel,
    config: config,
  };

  // ADVANCED BRAND PROMPT ENGINEERING
  let finalPrompt = params.prompt;
  if (params.brandIdentity?.isActive) {
    const brand = params.brandIdentity;
    const sysInstr = brand.systemInstruction ? `[SYSTEM PERSONA: ${brand.systemInstruction}]` : "";
    const prefix = brand.prefix || "";
    const suffix = brand.suffix || "";
    const neg = brand.negativeConstraints ? `[STRICT NEGATIVE CONSTRAINTS: ${brand.negativeConstraints}]` : "";

    const continuityAnchor = params.mode === GenerationMode.EXTEND_VIDEO 
      ? "\n[CONTEXT CONTINUITY: Reference the provided video as the absolute source of truth for character features, clothing, vocal movements, and environmental layout. Continue the scene seamlessly.]"
      : "";

    finalPrompt = `${sysInstr}\n${neg}${continuityAnchor}\n\n${prefix}${params.prompt}${suffix}`;
  }
  
  if (finalPrompt) {
    generateVideoPayload.prompt = finalPrompt;
  }

  const referenceImagesPayload: VideoGenerationReferenceImage[] = [];

  // 1. Add Brand Identity persistent assets (Internal)
  if (params.brandIdentity?.isActive && params.brandIdentity.referenceImages.length > 0) {
    for (const img of params.brandIdentity.referenceImages) {
      if (referenceImagesPayload.length < 3) {
        referenceImagesPayload.push({
          image: { imageBytes: img.base64, mimeType: img.file.type },
          referenceType: VideoGenerationReferenceType.ASSET,
        });
      }
    }
  }

  // 2. Add User Scene references (Persistent through extensions)
  if (params.referenceImages) {
    for (const img of params.referenceImages) {
      if (referenceImagesPayload.length < 3) {
        referenceImagesPayload.push({
          image: { imageBytes: img.base64, mimeType: img.file.type },
          referenceType: VideoGenerationReferenceType.ASSET,
        });
      }
    }
  }

  // 3. Handle Mode Specifics
  if (params.mode === GenerationMode.FRAMES_TO_VIDEO) {
    if (params.startFrame) {
      generateVideoPayload.image = {
        imageBytes: params.startFrame.base64,
        mimeType: params.startFrame.file.type,
      };
    }
    const finalEndFrame = params.isLooping ? params.startFrame : params.endFrame;
    if (finalEndFrame) {
      generateVideoPayload.config.lastFrame = {
        imageBytes: finalEndFrame.base64,
        mimeType: finalEndFrame.file.type,
      };
    }
  } else if (params.mode === GenerationMode.EXTEND_VIDEO) {
    if (params.inputVideoObject) {
      generateVideoPayload.video = params.inputVideoObject;
      if (params.isLooping && params.loopBackFrame) {
        generateVideoPayload.config.lastFrame = {
          imageBytes: params.loopBackFrame.base64,
          mimeType: params.loopBackFrame.file.type,
        };
      }
    } else {
      throw new Error('An input video object is required to extend a video.');
    }
  }

  if (referenceImagesPayload.length > 0) {
    generateVideoPayload.config.referenceImages = referenceImagesPayload;
  }

  console.log('Final Prepared Payload:', generateVideoPayload);
  let operation;
  try {
    operation = await ai.models.generateVideos(generateVideoPayload);
  } catch (err: any) {
    if (err.status === 429 || err.message?.includes('429')) {
      throw new Error('Quota Exhausted (429): You have reached your video generation limit for now. Please check your billing or wait a few minutes.');
    }
    throw err;
  }

  while (!operation.done) {
    await new Promise((resolve) => setTimeout(resolve, 10000));
    try {
      operation = await ai.operations.getVideosOperation({operation: operation});
    } catch (err: any) {
      if (err.status === 429) {
        throw new Error('Quota Exhausted (429): Rate limited while checking progress.');
      }
      throw err;
    }
  }

  if (operation?.response && operation.response.generatedVideos && operation.response.generatedVideos.length > 0) {
    const firstVideo = operation.response.generatedVideos[0];
    const videoObject = firstVideo.video;
    
    if (!videoObject || !videoObject.uri) {
      throw new Error('Generated video object or URI is missing.');
    }

    const url = decodeURIComponent(videoObject.uri);
    
    // Re-fetch with latest process.env.API_KEY
    const res = await fetch(`${url}&key=${process.env.API_KEY}`);

    if (res.status === 429) {
       throw new Error('Quota Exhausted (429): Unable to download video due to high traffic/quota limits.');
    }
    if (!res.ok) throw new Error(`Failed to fetch video: ${res.status}`);

    const videoBlob = await res.blob();
    const objectUrl = URL.createObjectURL(videoBlob);
    return {objectUrl, blob: videoBlob, uri: url, video: videoObject};
  } else {
    throw new Error('Operation failed or was cancelled.');
  }
};
